import React, { useState, useMemo } from 'react';
import { 
  ShieldCheck, 
  AlertTriangle, 
  FileText, 
  Search, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  User, 
  BarChart2,
  Download,
  Eye,
  X
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Type Definitions
interface KYCRecord {
  id: string;
  customerName: string;
  status: 'verified' | 'pending' | 'rejected';
  lastVerified: string;
  documentType: string;
}

interface AMLAlert {
  id: string;
  date: string;
  customerName: string;
  riskLevel: 'low' | 'medium' | 'high';
  description: string;
  status: 'open' | 'investigated' | 'resolved';
}

interface AuditLog {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  ipAddress: string;
  status: 'success' | 'failed';
}

interface SecurityAlert {
  id: string;
  type: 'login' | 'transaction' | 'data_access';
  severity: 'low' | 'medium' | 'high';
  description: string;
  timestamp: string;
  status: 'new' | 'acknowledged' | 'resolved';
}

// Modal Components
const KYCDetailsModal: React.FC<{
  record: KYCRecord;
  onClose: () => void;
}> = ({ record, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">KYC Details</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <span className="text-gray-500 block">Customer Name</span>
            <p className="font-semibold">{record.customerName}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Document Type</span>
            <p>{record.documentType}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Verification Status</span>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              record.status === 'verified'
                ? 'bg-green-100 text-green-800'
                : record.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
            </span>
          </div>
          
          <div>
            <span className="text-gray-500 block">Last Verified</span>
            <p>{record.lastVerified}</p>
          </div>
          
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">Verification Notes</h3>
            <p className="text-sm text-gray-600">
              {record.status === 'verified' 
                ? 'All documents have been successfully verified.' 
                : record.status === 'pending'
                ? 'Verification is currently in progress. Additional documentation may be required.'
                : 'Verification process was unsuccessful. Please review submitted documents.'}
            </p>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end space-x-2">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const AMLAlertDetailsModal: React.FC<{
  alert: AMLAlert;
  onClose: () => void;
  onResolve: (id: string) => void;
}> = ({ alert, onClose, onResolve }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">AML Alert Details</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <span className="text-gray-500 block">Customer Name</span>
            <p className="font-semibold">{alert.customerName}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Date</span>
            <p>{alert.date}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Risk Level</span>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              alert.riskLevel === 'high'
                ? 'bg-red-100 text-red-800'
                : alert.riskLevel === 'medium'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
            }`}>
              {alert.riskLevel.charAt(0).toUpperCase() + alert.riskLevel.slice(1)}
            </span>
          </div>
          
          <div>
            <span className="text-gray-500 block">Description</span>
            <p className="text-sm">{alert.description}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Current Status</span>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              alert.status === 'open'
                ? 'bg-red-100 text-red-800'
                : alert.status === 'investigated'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
            }`}>
              {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
            </span>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end space-x-2">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Close
          </button>
          {alert.status !== 'resolved' && (
            <button 
              onClick={() => onResolve(alert.id)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Mark as Resolved
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SecurityAlertDetailsModal: React.FC<{
  alert: SecurityAlert;
  onClose: () => void;
  onAcknowledge: (id: string) => void;
}> = ({ alert, onClose, onAcknowledge }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Security Alert Details</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <span className="text-gray-500 block">Alert Type</span>
            <p className="capitalize">{alert.type.replace('_', ' ')}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Timestamp</span>
            <p>{alert.timestamp}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Severity</span>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              alert.severity === 'high'
                ? 'bg-red-100 text-red-800'
                : alert.severity === 'medium'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
            }`}>
              {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
            </span>
          </div>
          
          <div>
            <span className="text-gray-500 block">Description</span>
            <p className="text-sm">{alert.description}</p>
          </div>
          
          <div>
            <span className="text-gray-500 block">Current Status</span>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              alert.status === 'new'
                ? 'bg-red-100 text-red-800'
                : alert.status === 'acknowledged'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-green-100 text-green-800'
            }`}>
              {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
            </span>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end space-x-2">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Close
          </button>
          {alert.status === 'new' && (
            <button 
              onClick={() => onAcknowledge(alert.id)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Acknowledge
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ComplianceAndSecurity: React.FC = () => {
  // Breadcrumb Management
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Compliance and Security' }
      ]);
  // Initial State
  const [kycRecords, setKycRecords] = useState<KYCRecord[]>([
    {
      id: 'KYC001',
      customerName: 'John Doe',
      status: 'verified',
      lastVerified: '2024-02-10',
      documentType: 'Passport'
    },
    {
      id: 'KYC002',
      customerName: 'Jane Smith',
      status: 'pending',
      lastVerified: '2024-01-15',
      documentType: 'Driver\'s License'
    }
  ]);

  const [amlAlerts, setAmlAlerts] = useState<AMLAlert[]>([
    {
      id: 'AML001',
      date: '2024-02-12',
      customerName: 'Mike Johnson',
      riskLevel: 'medium',
      description: 'Unusual transaction pattern detected',
      status: 'open'
    }
  ]);

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([
    {
      id: 'AUDIT001',
      timestamp: '2024-02-13 10:30:45',
      user: 'admin@company.com',
      action: 'Login',
      ipAddress: '192.168.1.100',
      status: 'success'
    }
  ]);

  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([
    {
      id: 'SEC001',
      type: 'login',
      severity: 'medium',
      description: 'Multiple failed login attempts',
      timestamp: '2024-02-13 09:15:22',
      status: 'new'
    }
  ]);

  // Modal State
  const [selectedKYCRecord, setSelectedKYCRecord] = useState<KYCRecord | null>(null);
  const [selectedAMLAlert, setSelectedAMLAlert] = useState<AMLAlert | null>(null);
  const [selectedSecurityAlert, setSelectedSecurityAlert] = useState<SecurityAlert | null>(null);
  const [isKYCModalOpen, setIsKYCModalOpen] = useState(false);
  const [isAMLModalOpen, setIsAMLModalOpen] = useState(false);
  const [isSecurityAlertModalOpen, setIsSecurityAlertModalOpen] = useState(false);

  // State for Filters and Search
  const [kycFilter, setKycFilter] = useState('all');
  const [kycSearch, setKycSearch] = useState('');
  const [amlFilter, setAmlFilter] = useState('all');
  const [amlSearch, setAmlSearch] = useState('');

  // Modal Handlers
  const handleResolveAMLAlert = (id: string) => {
    setAmlAlerts(prevAlerts => 
      prevAlerts.map(alert => 
        alert.id === id 
          ? { ...alert, status: 'resolved' } 
          : alert
      )
    );
    setIsAMLModalOpen(false);
  };

  const handleAcknowledgeSecurityAlert = (id: string) => {
    setSecurityAlerts(prevAlerts => 
      prevAlerts.map(alert => 
        alert.id === id 
          ? { ...alert, status: 'acknowledged' } 
          : alert
      )
    );
    setIsSecurityAlertModalOpen(false);
  };

  // Compliance Summary Calculations
  const complianceSummary = useMemo(() => {
    return {
      kycVerificationRate: (
        kycRecords.filter(record => record.status === 'verified').length / 
        kycRecords.length
      ) * 100,
      openAMLAlerts: amlAlerts.filter(alert => alert.status === 'open').length,
      highRiskAMLAlerts: amlAlerts.filter(alert => alert.riskLevel === 'high').length,
      newSecurityAlerts: securityAlerts.filter(alert => alert.status === 'new').length
    };
  }, [kycRecords, amlAlerts, securityAlerts]);

  // Render Methods
  const renderComplianceSummary = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
        <BarChart2 className="mr-2 h-5 w-5 text-purple-500" /> 
        Compliance Overview
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">KYC Verification Rate</p>
              <p className="text-xl font-bold text-green-600">
                {complianceSummary.kycVerificationRate.toFixed(1)}%
              </p>
            </div>
            <ShieldCheck className="h-6 w-6 text-green-500" />
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Open AML Alerts</p>
              <p className="text-xl font-bold text-yellow-600">
                {complianceSummary.openAMLAlerts}
              </p>
            </div>
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">New Security Alerts</p>
              <p className="text-xl font-bold text-red-600">
                {complianceSummary.newSecurityAlerts}
              </p>
            </div>
            <XCircle className="h-6 w-6 text-red-500" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderKYCVerification = () => {
    // Filter KYC Records
    const filteredKYCRecords = kycRecords.filter(record => {
      const matchesStatus = kycFilter === 'all' || record.status === kycFilter;
      const matchesSearch = record.customerName.toLowerCase().includes(kycSearch.toLowerCase());
      return matchesStatus && matchesSearch;
    });

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <User className="mr-2 h-5 w-5 text-blue-500" /> 
            KYC Verification
          </h2>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search customers"
                value={kycSearch}
                onChange={(e) => setKycSearch(e.target.value)}
                className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-2 top-3 h-4 w-4 text-gray-400" />
            </div>
            <select
              value={kycFilter}
              onChange={(e) => setKycFilter(e.target.value)}
              className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Verified
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredKYCRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {record.customerName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {record.documentType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {record.lastVerified}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      record.status === 'verified'
                        ? 'bg-green-100 text-green-800'
                        : record.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => {
                        setSelectedKYCRecord(record);
                        setIsKYCModalOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-900 flex items-center justify-end"
                    >
                      <Eye className="mr-1 h-4 w-4" /> View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAMLMonitoring = () => {
    // Filter AML Alerts
    const filteredAMLAlerts = amlAlerts.filter(alert => {
      const matchesStatus = amlFilter === 'all' || alert.status === amlFilter;
      const matchesSearch = alert.customerName.toLowerCase().includes(amlSearch.toLowerCase());
      return matchesStatus && matchesSearch;
    });

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="mr-2 h-5 w-5 text-red-500" /> 
            AML Monitoring
          </h2>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search alerts"
                value={amlSearch}
                onChange={(e) => setAmlSearch(e.target.value)}
                className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-2 top-3 h-4 w-4 text-gray-400" />
            </div>
            <select
              value={amlFilter}
              onChange={(e) => setAmlFilter(e.target.value)}
              className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="investigated">Investigated</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAMLAlerts.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {alert.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {alert.customerName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      alert.riskLevel === 'high'
                        ? 'bg-red-100 text-red-800'
                        : alert.riskLevel === 'medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {alert.riskLevel.charAt(0).toUpperCase() + alert.riskLevel.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {alert.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      alert.status === 'open'
                        ? 'bg-red-100 text-red-800'
                        : alert.status === 'investigated'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => {
                        setSelectedAMLAlert(alert);
                        setIsAMLModalOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-900 flex items-center justify-end"
                    >
                      <Eye className="mr-1 h-4 w-4" /> Investigate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAuditLogs = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="mr-2 h-5 w-5 text-blue-500" /> 
            Audit Logs
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Download className="mr-1 h-4 w-4" /> Export Logs
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {auditLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.timestamp}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {log.user}
                  </td>
                  <td className="pxpy-4 whitespace-nowrap text-sm text-gray-500">
                    {log.action}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.ipAddress}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      log.status === 'success'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSecurityAlerts = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="mr-2 h-5 w-5 text-red-500" /> 
            Security Alerts
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Clock className="mr-1 h-4 w-4" /> Review All
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {securityAlerts.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {alert.timestamp}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {alert.type.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      alert.severity === 'high'
                        ? 'bg-red-100 text-red-800'
                        : alert.severity === 'medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {alert.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      alert.status === 'new'
                        ? 'bg-red-100 text-red-800'
                        : alert.status === 'acknowledged'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      onClick={() => {
                        setSelectedSecurityAlert(alert);
                        setIsSecurityAlertModalOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-900 flex items-center justify-end"
                    >
                      <Eye className="mr-1 h-4 w-4" /> Investigate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderComplianceDocumentation = () => {
    const complianceDocuments = [
      {
        name: 'AML Policy',
        type: 'Policy Document',
        lastUpdated: '2024-01-15',
        version: '3.2'
      },
      {
        name: 'KYC Procedure Manual',
        type: 'Procedure Guide',
        lastUpdated: '2024-02-01',
        version: '2.7'
      }
    ];

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="mr-2 h-5 w-5 text-blue-500" /> 
            Compliance Documentation
          </h2>
          <button className="text-blue-600 hover:text-blue-800 flex items-center">
            <Download className="mr-1 h-4 w-4" /> Download All
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {complianceDocuments.map((doc, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {doc.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {doc.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {doc.lastUpdated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {doc.version}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900 flex items-center justify-end">
                      <Download className="mr-1 h-4 w-4" /> Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Compliance & Security
          </h1>

          {/* Compliance Summary */}
          {renderComplianceSummary()}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* KYC Verification */}
            <div>
              {renderKYCVerification()}
            </div>

            {/* AML Monitoring */}
            <div>
              {renderAMLMonitoring()}
            </div>
          </div>

          {/* Security and Audit Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Security Alerts */}
            <div>
              {renderSecurityAlerts()}
            </div>

            {/* Audit Logs */}
            <div>
              {renderAuditLogs()}
            </div>
          </div>

          {/* Compliance Documentation */}
          <div>
            {renderComplianceDocumentation()}
          </div>

          {/* Modals */}
          {isKYCModalOpen && selectedKYCRecord && (
            <KYCDetailsModal 
              record={selectedKYCRecord} 
              onClose={() => setIsKYCModalOpen(false)} 
            />
          )}

          {isAMLModalOpen && selectedAMLAlert && (
            <AMLAlertDetailsModal 
              alert={selectedAMLAlert} 
              onClose={() => setIsAMLModalOpen(false)}
              onResolve={handleResolveAMLAlert}
            />
          )}

          {isSecurityAlertModalOpen && selectedSecurityAlert && (
            <SecurityAlertDetailsModal 
              alert={selectedSecurityAlert} 
              onClose={() => setIsSecurityAlertModalOpen(false)}
              onAcknowledge={handleAcknowledgeSecurityAlert}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceAndSecurity;